export const BIOMATCH_FAILURE_REASONS = {
  documentFaceNotDetected: 'document_face_not_detected',
  selfieFaceNotDetected: 'selfie_face_not_detected',
  multipleFaces: 'multiple_faces',
  facesDoNotMatch: 'faces_do_not_match',
  verificationFailed: 'verification_failed',
  processingFailed: 'processing_failed',
} as const;

export type BiomatchFailureReason =
  (typeof BIOMATCH_FAILURE_REASONS)[keyof typeof BIOMATCH_FAILURE_REASONS];

export const BIOMATCH_REASON_CODES = {
  faceNotDetected: 'face_not_detected',
  multipleFaces: 'multiple_faces',
  facesDoNotMatch: 'faces_do_not_match',
  verificationFailed: 'verification_failed',
  processingFailed: 'processing_failed',
} as const;

export type BiomatchCheck = 'document' | 'selfie' | 'comparison';

export type BiomatchFailure = {
  reason: BiomatchFailureReason;
  titleKey: string;
  descriptionKey: string;
};

const FAILURE_COPY: Record<BiomatchFailureReason, Omit<BiomatchFailure, 'reason'>> = {
  [BIOMATCH_FAILURE_REASONS.documentFaceNotDetected]: {
    titleKey: 'biomatch_document_face_not_detected_title',
    descriptionKey: 'biomatch_document_face_not_detected_text',
  },
  [BIOMATCH_FAILURE_REASONS.selfieFaceNotDetected]: {
    titleKey: 'biomatch_selfie_face_not_detected_title',
    descriptionKey: 'biomatch_selfie_face_not_detected_text',
  },
  [BIOMATCH_FAILURE_REASONS.multipleFaces]: {
    titleKey: 'biomatch_multiple_faces_title',
    descriptionKey: 'biomatch_multiple_faces_text',
  },
  [BIOMATCH_FAILURE_REASONS.facesDoNotMatch]: {
    titleKey: 'biomatch_faces_do_not_match_title',
    descriptionKey: 'biomatch_faces_do_not_match_text',
  },
  [BIOMATCH_FAILURE_REASONS.verificationFailed]: {
    titleKey: 'biomatch_verification_failed_title',
    descriptionKey: 'biomatch_verification_failed_text',
  },
  [BIOMATCH_FAILURE_REASONS.processingFailed]: {
    titleKey: 'biomatch_processing_failed_title',
    descriptionKey: 'biomatch_processing_failed_text',
  },
};

function getFaceNotDetectedReason(check: BiomatchCheck): BiomatchFailureReason {
  if (check === 'document') {
    return BIOMATCH_FAILURE_REASONS.documentFaceNotDetected;
  }
  if (check === 'selfie') {
    return BIOMATCH_FAILURE_REASONS.selfieFaceNotDetected;
  }
  return BIOMATCH_FAILURE_REASONS.verificationFailed;
}

export function getBiomatchFailureReason(
  reasonCode: string | null | undefined,
  check: BiomatchCheck,
): BiomatchFailureReason | null {
  switch (reasonCode) {
    case BIOMATCH_REASON_CODES.faceNotDetected:
      return getFaceNotDetectedReason(check);
    case BIOMATCH_REASON_CODES.multipleFaces:
      return BIOMATCH_FAILURE_REASONS.multipleFaces;
    case BIOMATCH_REASON_CODES.facesDoNotMatch:
      return BIOMATCH_FAILURE_REASONS.facesDoNotMatch;
    case BIOMATCH_REASON_CODES.verificationFailed:
      return BIOMATCH_FAILURE_REASONS.verificationFailed;
    case BIOMATCH_REASON_CODES.processingFailed:
      return BIOMATCH_FAILURE_REASONS.processingFailed;
    default:
      return null;
  }
}

export function getBiomatchFailure(
  reasonCode: string | null | undefined,
  check: BiomatchCheck,
): BiomatchFailure | null {
  const reason = getBiomatchFailureReason(reasonCode, check);
  return reason ? {reason, ...FAILURE_COPY[reason]} : null;
}
